import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { translations } from '../../../configs/translations'
import { UserProfile } from '../../../models/UserProfile'
import UserService from '../../../services/UserService'
import { useStore } from '../../../store/store'
import { formatTextWithParams } from '../../../utils/formatTextWithParams'
import { DonatePages } from '../DonateShop'
import './DonateTo.css'

const DP_TO_MONEY_RATE = 400
const DP_TO_XP_RATE = 25
const MAXIMUM_DP = 1_000_000_000

export interface DonateToProps {
    dpAmount: number
    toItemAmount: number
    setModuleWindowTitle: (title: string) => void
    setModuleWindowText: (text: string) => void
    setModuleWindowOpen: (open: boolean) => void
    isAccepted: boolean
    page: DonatePages
    setProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>
}

export const DonateTo = ({
    setModuleWindowOpen,
    setModuleWindowTitle,
    setModuleWindowText,
    isAccepted,
    dpAmount,
    toItemAmount,
    page,
    setProfile,
}: DonateToProps) => {

    const [dp, setDp] = useState<number | null>(null)
    const [money, setMoney] = useState<number | null>(null)

    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]


    useEffect(() => {
        if (dp === null || dp === undefined) {
            setMoney(null)
            return
        }

        let newDp = dp
        if (dp < 0) {
            newDp = 0
        } else if (dp > MAXIMUM_DP) {
            newDp = MAXIMUM_DP
        }
        if (newDp !== dp) {
            setDp(newDp)
            return
        }
        if (page === 'money') {
            setMoney(newDp * DP_TO_MONEY_RATE)
        } else {
            setMoney(newDp * DP_TO_XP_RATE)
        }
    }, [dp])

    useEffect(() => {
        if (money) {
            if (money < 0) {
                setMoney(0)
            } else if (money > MAXIMUM_DP * DP_TO_MONEY_RATE) {
                setMoney(MAXIMUM_DP * DP_TO_MONEY_RATE)
            }
            setDp(money / (page === 'money' ? DP_TO_MONEY_RATE : DP_TO_XP_RATE))
        } else {
            setDp(null)
        }
    }, [money])

    const getText = () => {
        return formatTextWithParams(page === 'money' ? t.donate_to_money_warning_text : t.donate_to_exp_warning_text,
            [dp?.toString() || '', money?.toString() || '']
        )
    }

    const handleConvert = () => {
        if (!dp || !money) {
            return
        }
        setModuleWindowOpen(true)
        if (page === 'money') {
            setModuleWindowTitle(t.donate_to_money_warning_title)
            setModuleWindowText(getText())
        } else {
            setModuleWindowTitle(t.donate_to_exp_warning_title)
            setModuleWindowText(getText())
        }
    }

    useEffect(() => {
        let audio: HTMLAudioElement | null = new Audio('../sounds/buy.mp3')

        const convertDpToMoney = async () => {
            await UserService.convertDpTo(dp!, 'DONATE_TO_MONEY')
                .then(() => {
                    audio?.play()
                    setModuleWindowOpen(false)
                    setProfile(null)
                })
                .catch((error) => {
                    toast.error(
                        error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                            ? t[error.response.data as keyof typeof t]
                            : t.error_unknown,
                        { className: 'notification-error' }
                    )
                })
        }

        const convertDpToExp = async () => {
            await UserService.convertDpTo(dp!, 'DONATE_TO_XP')
                .then(() => {
                    audio?.play()
                    setModuleWindowOpen(false)
                    setProfile(null)
                })
                .catch((error) => {
                    toast.error(
                        error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                            ? t[error.response.data as keyof typeof t]
                            : t.error_unknown,
                        { className: 'notification-error' }
                    )
                })
        }

        if (isAccepted) {
            if (page === 'money') {
                convertDpToMoney()
            } else {
                convertDpToExp()
            }
            setModuleWindowOpen(false)
            setDp(null)
            setMoney(null)
        }
    }, [isAccepted])

    useEffect(() => {
        setDp(null)
        setMoney(null)
    }, [page])

    return (
        <div className="donate-to-container">
            <div className="donate-to-header">
                <h1>{t.donate_to_money}</h1>
            </div>
            <div className="donate-to-content-wrapper">
                <div className="donate-to-content">

                    <div className="donate-to-money-points-container">
                        <div className="donate-to-money-points-header">
                            <p>{t.donate_points}</p>
                            <p className="donate-to-money-points-header-value">{dpAmount}</p>
                        </div>

                        <div className="donate-to-money-points-icon">
                            <img src="../icons/256px/dp.png" alt="donate points" />
                        </div>

                        <div className="donate-to-money-points-input-container">
                            <input
                                className="donate-to-money-points-input"
                                type="number"
                                placeholder={t.dp_amount}
                                value={dp || ''}
                                onChange={(e) => setDp(Number(e.target.value))}
                                onBlur={() => setDp(Math.ceil(dp!))}
                            />
                        </div>
                    </div>

                    <div className="donate-to-money-arrow-container">
                        <img src="../icons/arrow.png" alt="arrow" />
                    </div>

                    <div className="donate-to-money-points-container">
                        <div className="donate-to-money-points-header">
                            <p>{page === 'money' ? t.money : t.xp}</p>
                            <p className='donate-to-money-points-header-value'>{toItemAmount}</p>
                        </div>

                        <div className="donate-to-money-points-icon">
                            <img src={`../icons/256px/${page === 'money' ? 'money' : 'xp'}.png`} alt="donate points" />
                        </div>

                        <div className="donate-to-money-points-input-container">
                            <input
                                className="donate-to-money-points-input"
                                type="number"
                                placeholder={t.money_amount}
                                value={money || ''}
                                onChange={(e) => setMoney(Number(e.target.value))}
                                onBlur={() => {
                                    setMoney(Math.ceil(money!))
                                    setDp(Math.ceil(money! / DP_TO_MONEY_RATE))
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="donate-to-money-convert-button-container">
                    <div
                        onClick={handleConvert}
                        className="donate-to-money-convert-button"
                    >
                        <p>{t.convert}</p>
                    </div>
                </div>
            </div>
        </div>
    )
}